import { useId, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import type { StreakTier } from '../model/streakTier';

/** Цвета по ступеням `streakTier`: 0 — погасший серый, дальше по «температуре» пламени. */
const PALETTE: Record<
  StreakTier,
  { outer: [string, string]; inner: [string, string]; text: string }
> = {
  0: { outer: ['#d5dbe2', '#b9c1cb'], inner: ['#eef1f5', '#d9dee5'], text: '#8a93a1' },
  // 1–9 дней — обычный оранжевый огонь.
  1: { outer: ['#ffb238', '#ff6a00'], inner: ['#fff3a3', '#ffc21a'], text: '#b93d00' },
  // 10–29 — красный.
  2: { outer: ['#ff6a4d', '#e0161b'], inner: ['#ffd07a', '#ff7a2e'], text: '#9f1010' },
  // 30–59 — малиновый.
  3: { outer: ['#ff7ac2', '#e0117a'], inner: ['#ffe0f0', '#ff9bd0'], text: '#9d0b56' },
  // 60–99 — фиолетовый, в цвет заполненных дней календаря.
  4: { outer: ['#b18cff', '#7a2ff7'], inner: ['#efe4ff', '#c3a3ff'], text: '#4c1d95' },
  // 100–179 — синий.
  5: { outer: ['#6aa8ff', '#1f5bff'], inner: ['#e3f1ff', '#8cc6ff'], text: '#12398f' },
  // 180–364 — ледяной, самый «горячий» огонь.
  6: { outer: ['#7fe7ff', '#0fa9cf'], inner: ['#ffffff', '#bff6ff'], text: '#075e70' },
  // 365+ — золото, год без пропусков.
  7: { outer: ['#ffe27a', '#e8a400'], inner: ['#fffbe0', '#ffe680'], text: '#7a5200' },
};

interface FlameProps {
  tier: StreakTier;
  className?: string;
  /** Показ внутри огня — крупная цифра стрика в шторке. */
  children?: ReactNode;
}

/** Огонёк стрика — SVG, анимация на CSS (`anim-flame-*` в index.css), у погасшего стоит. */
export function Flame({ tier, className, children }: FlameProps) {
  const id = useId();
  const { outer, inner, text } = PALETTE[tier];
  const lit = tier > 0;

  return (
    <span className={cn('relative inline-flex items-center justify-center', className)}>
      <svg viewBox="0 0 100 120" className="relative size-full overflow-visible" aria-hidden="true">
        <defs>
          <linearGradient id={`${id}-o`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={outer[0]} />
            <stop offset="1" stopColor={outer[1]} />
          </linearGradient>
          <linearGradient id={`${id}-i`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={inner[0]} />
            <stop offset="1" stopColor={inner[1]} />
          </linearGradient>
        </defs>
        <path
          className={cn(lit && 'anim-flame-outer')}
          d="M44 14c3 16 16 24 24 34 3-6 3-12 2-18 12 12 20 28 20 46 0 25-18 42-40 42S10 101 10 76c0-15 7-27 16-36 1 8 4 13 9 16-3-15 0-30 9-42z"
          fill={`url(#${id}-o)`}
        />
        <path
          className={cn(lit && 'anim-flame-inner')}
          d="M52 54c4 12 17 18 17 36 0 14-9 24-19 24s-19-10-19-24c0-9 5-16 10-21 1 5 3 8 6 9-1-9 1-17 5-24z"
          fill={`url(#${id}-i)`}
        />
      </svg>
      {children != null && (
        // Центр числа — на ~68% высоты: там самая широкая часть огня, а не
        // геометрическая середина рамки (верх у огня узкий).
        <span
          className="absolute inset-x-0 top-[40%] bottom-[4%] flex items-center justify-center"
          style={{ color: text }}
        >
          {children}
        </span>
      )}
    </span>
  );
}
